import React, { useState, useRef } from 'react';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { patch } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';

// Social tab module: expects full website object
function Social({ website, onUpdated }) {
  const hasWebsite = !!website;
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState((website && (website.socials.items || [])) || []);
  const [dragIndex, setDragIndex] = useState(-1);
  const [overIndex, setOverIndex] = useState(-1);
  const itemRefs = useRef([]);

  const socialOptions = [
    'Facebook', 'Twitter', 'LinkedIn', 'GitHub', 'Instagram', 'YouTube', 'Tumblr', 'Pinterest', 'Reddit', 'Telegram', 'Email', 'Phone'
  ];

  const startEdit = () => {
    setItems([...(website.socials.items || [])]);
    setError('');
    setEdit(true);
  };

  const cancelEdit = () => {
    setError('');
    setEdit(false);
  };

  const addItem = () => {
    setItems(prev => [...prev, { text: '', url: '', social: '' }]);
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const updateField = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const moveItem = (from, to) => {
    setItems(prev => {
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const moveUp = (idx) => moveItem(idx, idx - 1);
  const moveDown = (idx) => moveItem(idx, idx + 1);

  // Drag & Drop sorting handlers
  const onDragStart = (idx, e) => {
    setDragIndex(idx);
    setOverIndex(idx);
    try {
      e.dataTransfer.setData('text/plain', String(idx));
    } catch (_) {}
    e.dataTransfer.effectAllowed = 'move';
    // Use the full card as the drag image so the user sees the element moving
    const el = itemRefs.current[idx];
    if (el && typeof e.dataTransfer.setDragImage === 'function') {
      // Slight offset so the cursor isn't on the very edge
      e.dataTransfer.setDragImage(el, 16, 16);
    }
  };

  const onDragEnter = (idx) => {
    if (idx !== overIndex) setOverIndex(idx);
  };

  const onDragOver = (e) => {
    e.preventDefault(); // allow drop
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = (idx, e) => {
    e.preventDefault();
    const from = dragIndex;
    const to = idx;
    if (from !== -1 && to !== -1 && from !== to) {
      moveItem(from, to);
    }
    setDragIndex(-1);
    setOverIndex(-1);
  };

  const onDragEnd = () => {
    setDragIndex(-1);
    setOverIndex(-1);
  };

  const isHttpUrl = (u) => /^(https?:)\/\//i.test((u || '').trim());
  const isMailto = (u) => /^mailto:/i.test((u || '').trim());
  const isTel = (u) => /^tel:/i.test((u || '').trim());

  const validateUrlForPlatform = (platform, url) => {
    const val = (url || '').trim();
    if (platform === 'Email') return isMailto(val) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
    if (platform === 'Phone') return isTel(val) || /^\+?[0-9\s().-]{5,}$/.test(val);
    return isHttpUrl(val);
  };

  const save = async (e) => {
    e.preventDefault();
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (!it.social || !socialOptions.includes(it.social)) {
        toast.error(`Row ${i + 1}: platform is required`);
        return;
      }
      if (!it.url || !validateUrlForPlatform(it.social, it.url)) {
        toast.error(`Row ${i + 1}: url is invalid for ${it.social}`);
        return;
      }
    }

    try {
      setSaving(true);
      setError('');
      const payload = { socials: items };
      const resp = await patch(`websites/${website.id}/socials`, payload);
      const updated = resp?.website || resp || { ...website, socials: items };
      onUpdated?.(updated);
      setEdit(false);
    } catch (e) {
      toast.error('Failed to save socials.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{marginTop: '2rem'}}>
      {!hasWebsite ? (
        <Alert variant="warning">Website not found.</Alert>
      ) : !edit ? (
        <>
          <p style={{ marginBottom: '0.75rem', color: '#64748b' }}>Connect social accounts.</p>
          {(!Array.isArray(website.socials.items) || website.socials.items.length === 0) ? (
            <em>No social links.</em>
          ) : (
            <ul style={{ marginBottom: 0 }}>
              {website.socials.items.map((it, idx) => (
                <li key={idx}><strong>{it.social || '-'}</strong>: {it.text || '-'} — {it.url || '-'}</li>
              ))}
            </ul>
          )}
          <div style={{ marginTop: '12px' }}>
            <Button className="login-button" size="sm" onClick={startEdit}>Edit</Button>
          </div>
        </>
      ) : (
        <>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form onSubmit={save}>
            {items.map((it, idx) => (
              <div
                key={idx}
                onDragEnter={() => onDragEnter(idx)}
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(idx, e)}
                style={{
                  border: '1px solid ' + (overIndex === idx && dragIndex !== -1 ? '#3b82f6' : '#e5e7eb'),
                  borderRadius: 10,
                  padding: 12,
                  paddingLeft: 36,
                  marginBottom: 10,
                  background: dragIndex === idx ? '#f8fafc' : 'white',
                  position: 'relative',
                  boxShadow: dragIndex === idx ? '0 8px 20px rgba(0,0,0,0.08)' : 'none',
                  transform: dragIndex === idx ? 'scale(1.01)' : 'none',
                  opacity: dragIndex === idx ? 0.92 : 1,
                  transition: 'box-shadow 120ms ease, transform 120ms ease, opacity 120ms ease'
                }}
                ref={(el) => (itemRefs.current[idx] = el)}
              >
                {/* Drop indicator line when hovering another card */}
                {dragIndex !== -1 && overIndex === idx && dragIndex !== idx && (
                  <div
                    style={{
                      position: 'absolute',
                      top: -1,
                      left: 0,
                      right: 0,
                      height: 2,
                      background: '#3b82f6'
                    }}
                  />
                )}
                {/* Drag handle icon (left side) */}
                <button
                  type="button"
                  aria-label="Drag to reorder"
                  title="Drag to reorder"
                  draggable
                  onDragStart={(e) => onDragStart(idx, e)}
                  onDragEnd={onDragEnd}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    width: 24,
                    height: 24,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'transparent',
                    border: 'none',
                    color: '#64748b',
                    fontSize: 16,
                    lineHeight: '16px',
                    cursor: dragIndex === idx ? 'grabbing' : 'grab',
                    transform: 'translateY(-50%)'
                  }}
                >
                  ⋮⋮
                </button>
                <button
                  type="button"
                  aria-label="Remove"
                  onClick={() => removeItem(idx)}
                  style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    width: 24,
                    height: 24,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f65279',
                    borderRadius: '0 .6rem',
                    border: 'none',
                    color: 'white',
                    fontSize: '1rem',
                    lineHeight: '18px',
                    cursor: 'pointer'
                  }}
                  title="Remove"
                >
                  ×
                </button>
                <div className="form-row-horizontal mb-2" style={{marginTop: "1rem"}}>
                  <Form.Label>Platform</Form.Label>
                  <Form.Select
                    value={it.social}
                    onChange={(e) => updateField(idx, 'social', e.target.value)}
                  >
                    <option value="">Select platform</option>
                    {socialOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </Form.Select>
                </div>
                <div className="form-row-horizontal">
                  <Form.Label>URL</Form.Label>
                  <Form.Control
                    type="text"
                    value={it.url}
                    onChange={(e) => updateField(idx, 'url', e.target.value)}
                    placeholder="https://... or mailto: or tel:"
                  />
                </div>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <Button type="button" className='primary-button' size="sm" onClick={addItem}>Add social</Button>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button type="submit" className="primary-button" disabled={saving}>
                {saving ? (<><Spinner animation="border" size="sm" className="me-2" /> Saving...</>) : 'Save'}
              </Button>
              <Button type="button" className='primary-button' onClick={cancelEdit} disabled={saving}>Cancel</Button>
            </div>
          </Form>
        </>
      )}
    </div>
  );
}

export default Social;
